'use client'
import { useSearchType } from '@/api/hooks'
import HomeGrid from '@/app/(site)/home-grid'
import { ClickableDecision, VideoSpotlightDecision, XlCarouselBannerDecision } from '@/app/api/placements/types'
import { useIsMobile } from '@/app/util/use-media-query'
import { useAdOffsets } from '@/components/grid/use-ad-offsets'
import { SearchTypeContext } from '@/context/search-type'
import useClientRender from '@/util/use-client-render'
import { ComponentProps, useContext } from 'react'
import { useWindowSize } from 'react-use'
import SpotlightGrid from '../spotlight-grid'
import ClickableAd from './clickable-ad'
import SpotlightContainer from './container'
import { clickableAdWidth } from './spotlight-util'
import VideoSpotlight from './video-spotlight'

type Props = {
    trendingGifs: ComponentProps<typeof HomeGrid>['trendingGifs']
    spotlightData?: VideoSpotlightDecision
    clickableAd?: ClickableDecision
    carouselAd?: XlCarouselBannerDecision
}

function AdGrid({ trendingGifs, spotlightData, clickableAd, carouselAd }: Props) {
    const spotlightGif = spotlightData?.gif
    const clickableAdGif = clickableAd?.gif
    const isVideo = spotlightGif?.video

    const type = useSearchType()
    const { isTransitioning } = useContext(SearchTypeContext)
    const isMobile = useIsMobile()

    const { width: windowWidth } = useWindowSize()

    const clientRender = useClientRender()
    const hasSpotlight = !!carouselAd || !!spotlightGif
    const includeSpotlight = hasSpotlight && type !== 'clips' && !(type === 'stickers' && !isVideo) && !isTransitioning
    const { columnOffsets } = useAdOffsets({
        clickableAdGif: clickableAdGif || null,
        spotlightGif: spotlightGif || null,
        carouselAd,
        includeSpotlight,
        isMobile,
        windowWidth,
        type,
        clientRender,
    })
    return (
        <>
            {!isTransitioning && (
                <>
                    {includeSpotlight && (
                        <SpotlightContainer>
                            {spotlightGif ? (
                                <VideoSpotlight data={spotlightData} />
                            ) : carouselAd ? (
                                <SpotlightGrid data={carouselAd} />
                            ) : null}
                        </SpotlightContainer>
                    )}
                    {clickableAdGif && clientRender && type === 'gifs' && (
                        <ClickableAd
                            ad={clickableAd}
                            gif={clickableAdGif}
                            width={isMobile ? windowWidth / 2 - 3 : clickableAdWidth}
                            isMobile={isMobile}
                        />
                    )}
                </>
            )}

            <HomeGrid trendingGifs={trendingGifs} columnOffsets={columnOffsets} hasClickableAd={!!clickableAd} />
        </>
    )
}

export default AdGrid
